import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { RootReducer } from "src/redux/reducers";
import { getDashboardInfoActions, getActivityFeedActions } from "src/redux/actions/dashboard";
import { getAssignmentsActions } from "src/redux/actions/assignments";
import { Layout, Loader } from "src/components";
import useSchools from "src/hooks/useSchools";
import { StyledGriffleDashboard } from "./GriffleDashboard.styles";
import GriffleReadingChart from "./GriffleReadingChart";
import { IconStudents, IconBooks, IconClock, IconQuiz } from "src/modules/griffle/GriffleIcons";
import ClassAtGlance from "src/modules/dashboard/ClassAtGlance";
import ActivityFeed, { ActivityItem } from "src/modules/dashboard/ActivityFeed";
import UpcomingAssignments from "src/modules/dashboard/UpcomingAssignments";
import { StudentListMode } from "src/redux/actions/reports/types";
import { ActivityFeedItemApi } from "src/redux/actions/dashboard/types";

const ACTIVITY_TYPE_MAP: Record<number, ActivityItem["type"]> = {
  2: "book_completed",
  6: "book_completed",
  9: "reading_session",
  11: "reading_session"
};

const formatTimeAgo = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString();
};

const mapApiToActivityItem = (item: ActivityFeedItemApi, index: number): ActivityItem => {
  const studentName = item.message || "Student";
  const activityName = item.activityItemName || "";
  const type = ACTIVITY_TYPE_MAP[item.type] || "reading_session";

  let action: string;
  switch (item.type) {
    case 2:
    case 6:
      action = activityName ? `Completed reading "${activityName}"` : "Completed reading";
      break;
    case 9:
    case 11:
      action = item.readingMinutes ? `Reading session - ${item.readingMinutes} minutes` : "Reading session";
      break;
    case 5:
      action = activityName ? `Earned achievement: ${activityName}` : "Earned an achievement";
      break;
    default:
      action = activityName || "Activity";
  }

  return {
    id: `${index}-${item.activityDate}`,
    studentName,
    action,
    timestamp: formatTimeAgo(item.activityDate),
    type
  };
};

const GriffleDashboard: React.FC = () => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [showDetails, setShowDetails] = useState(true);

  const {
    user: { userData, isLoading: isLoadingUser },
    dashboard: { dashboardData, isLoading: isLoadingDashboard, activityFeed, isLoadingActivityFeed },
    assignments: { assignments: rawAssignments }
  }: RootReducer = useSelector<RootReducer>((state) => state) as RootReducer;

  const { activeSchoolId, getGroups } = useSchools();
  const isLoading = isLoadingUser || isLoadingDashboard;

  const getDashboardInfo = useCallback(
    (payload: { schoolId: number }) => dispatch(getDashboardInfoActions.request(payload)),
    [dispatch]
  );

  useEffect(() => {
    if (activeSchoolId) {
      getDashboardInfo({ schoolId: activeSchoolId });
      dispatch(getActivityFeedActions.request({ schoolId: activeSchoolId, limit: 10 }));
      getGroups();
    }
  }, [activeSchoolId]);

  useEffect(() => {
    dispatch(getAssignmentsActions.request({ state: 2 }));
  }, [dispatch]);

  const activities: ActivityItem[] = useMemo(() => {
    if (!activityFeed?.rows) return [];
    return activityFeed.rows.map(mapApiToActivityItem);
  }, [activityFeed]);

  const handleClassClick = useCallback(
    (groupId: number) => {
      navigate("/griffle/progress-report-class", { state: { groupId } });
    },
    [navigate]
  );

  const classes = useMemo(() => dashboardData?.total?.classses ?? [], [dashboardData]);

  const upcomingAssignments = useMemo(() => {
    if (!rawAssignments) return [];
    return rawAssignments.map((a) => ({
      id: a.assignmentId,
      name: a.assignmentName || "-",
      dueDate: a.dueDate,
      groups: (a.groups ?? [])
        .map((g) => {
          if (typeof g === "string") return g;
          if (g && typeof g === "object") {
            const o = g as { name?: unknown; group?: unknown };
            const label = o.name ?? o.group;
            return typeof label === "string" ? label : "";
          }
          return "";
        })
        .filter((g) => g.length > 0)
        .join(", "),
      usersCompleted: a.usersCompleted ?? 0,
      usersAssigned: a.usersAssigned ?? 0
    }));
  }, [rawAssignments]);

  if (isLoading) {
    return (
      <Layout>
        <Loader />
      </Layout>
    );
  }

  const firstName = userData?.firstName ?? userData?.name ?? "";
  const stats = dashboardData?.total;

  return (
    <Layout>
      <StyledGriffleDashboard>
        {/* ── Header: greeting + inline stats ────────────── */}
        <div className="hero">
          <h1 className="page-title">
            {t("dashboard_page_title_welcome")}
            {firstName ? `, ${firstName}` : ""}
          </h1>
          <div className="hero-stats">
            <span className="hero-stat">
              <IconStudents size={16} />
              <strong>{stats?.numberOfStudents ?? 0}</strong> {t("dashboard_stat_students", "Students")}
            </span>
            <span className="hero-stat-divider" />
            <span className="hero-stat">
              <IconBooks size={16} />
              <strong>{stats?.totalBooksRead ?? 0}</strong> {t("dashboard_stat_books_read", "Books Read")}
            </span>
            <span className="hero-stat-divider" />
            <span className="hero-stat">
              <IconClock size={16} />
              <strong>{stats?.totalMinutesRead ?? 0}</strong> {t("dashboard_stat_minutes_read_short", "min")}
            </span>
            <span className="hero-stat-divider" />
            <span className="hero-stat">
              <IconQuiz size={16} />
              <strong>{stats?.averageScore != null ? `${Math.round(stats.averageScore)}%` : "—"}</strong>{" "}
              {t("dashboard_stat_avg_score", "Avg Score")}
              {stats?.averagePoints != null && (
                <>
                  {" / "}
                  <strong>{Math.round(stats.averagePoints)}</strong> {t("dashboard_stat_avg_points", "Pts")}
                </>
              )}
            </span>
          </div>
        </div>

        {/* ── Action cards ────────────────────────────────── */}
        <div className="action-cards">
          <Link className="action-card action-card--create" to="/griffle/assignments/create">
            <div className="action-card-icon">
              <svg viewBox="0 0 20 20" fill="none">
                <path d="M10 4V16M4 10H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </div>
            <div className="action-card-text">
              <span className="action-card-title">
                {t("dashboard_quick_action_create_assignment", "Create Assignment")}
              </span>
              <span className="action-card-desc">
                {t("dashboard_quick_action_create_assignment_desc", "Set up a new book or quiz assignment")}
              </span>
            </div>
          </Link>

          <Link className="action-card action-card--overdue" to="/griffle/assignments" state={{ filter: "overdue" }}>
            <div className="action-card-icon">
              <svg viewBox="0 0 20 20" fill="none">
                <path
                  d="M10 6V10L13 13M18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="action-card-text">
              <span className="action-card-title">{t("dashboard_quick_action_review_overdue", "Review Overdue")}</span>
              <span className="action-card-desc">
                {t("dashboard_quick_action_review_overdue_desc", "Check assignments past their due date")}
              </span>
            </div>
          </Link>

          <Link
            className="action-card action-card--attention"
            to="/griffle/progress-report-students"
            state={{ mode: StudentListMode.NEEDS_ATTENTION }}
          >
            <div className="action-card-icon">
              <svg viewBox="0 0 20 20" fill="none">
                <path
                  d="M10 2L2 18H18L10 2Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path d="M10 8V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <circle cx="10" cy="14" r="1" fill="currentColor" />
              </svg>
            </div>
            <div className="action-card-text">
              <span className="action-card-title">
                {t("dashboard_quick_action_students_attention", "Students Needing Attention")}
              </span>
              <span className="action-card-desc">
                {t("dashboard_quick_action_students_attention_desc", "Students with low activity or scores")}
              </span>
            </div>
          </Link>
        </div>

        {/* ── Classes at a Glance (primary focus) ─────────── */}
        {classes.length > 0 && <ClassAtGlance classes={classes} onClassClick={handleClassClick} />}

        {/* ── Upcoming Assignments ────────────────────────── */}
        <UpcomingAssignments assignments={upcomingAssignments} />

        {/* ── Expandable details section ──────────────────── */}
        <button className="toggle-details-btn" onClick={() => setShowDetails((prev) => !prev)} type="button">
          {showDetails
            ? t("dashboard_hide_details", "Hide details")
            : t("dashboard_show_details", "Show reading chart & activity")}
          <svg
            className={`toggle-chevron ${showDetails ? "open" : ""}`}
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
          >
            <polyline
              points="6 9 12 15 18 9"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {showDetails && (
          <div className="details-section">
            <div className="chart-panel">
              <div className="chart-panel-header">
                <div>
                  <h2 className="chart-panel-title">{t("dashboard_page_class_reading_table_title")}</h2>
                  <p className="chart-panel-subtitle">
                    {t("dashboard_chart_subtitle", "Number of students at each reading level")}
                  </p>
                </div>
              </div>
              <GriffleReadingChart data={dashboardData} />
            </div>

            <ActivityFeed activities={activities} isLoading={isLoadingActivityFeed} />
          </div>
        )}
      </StyledGriffleDashboard>
    </Layout>
  );
};

export default GriffleDashboard;
